import React, { useCallback, useMemo, useState } from "react";

import { FeatureFlags, Product } from "@design-controls/types";
import {
  createQColumnHelper,
  DataProvider,
  QBodyLayout,
  QBox,
  QButton,
  QButtonGroup,
  QDataTable,
  QDeleteConfirmationModal,
  QHeader,
  QResourceLabel,
  QText,
  QTitle,
  TableMenuItem,
  Filtering,
  useFeatureFlag,
} from "@qualio/ui-components";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";

import { useProducts } from "../../hooks/product/useProducts";
import { useResolveQriDetails } from "../../hooks/useResolveQriDetails";
import useHasPermission from "../../hooks/useHasPermission";
import { UserPermissions } from "../../domain/permissions";
import { Link } from "react-router-dom";

const columnHelper = createQColumnHelper<Product>();

export const Products: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const permissions = useHasPermission([UserPermissions.MANAGE_PRODUCT]);
  const createProductClick = useCallback(
    () => navigate("/product/create"),
    [navigate],
  );
  const onEditClick = useCallback(
    (product: Product) => navigate(`/product/${product.id}/config`),
    [navigate],
  );
  const {
    products: data,
    isLoading,
    deleteProduct,
    isDeleting,
  } = useProducts();

  const [productToDelete, setProductToBeDelete] = useState<Product | undefined>(
    undefined,
  );

  const linkedProductEnabled = Boolean(
    useFeatureFlag()[FeatureFlags.LINK_RESOURCE_LIBRARY_PRODUCT],
  );

  const productQris = useMemo(() => {
    return linkedProductEnabled
      ? data.flatMap((product) =>
          product.productQri ? [product.productQri] : [],
        )
      : [];
  }, [data, linkedProductEnabled]);
  const { details: productDetails, isLoading: isResolvingProductNames } =
    useResolveQriDetails(productQris);

  const filterOption = useCallback(
    (item: Product, searchTerm: string | undefined) => {
      if (searchTerm) {
        const term = searchTerm.toLocaleLowerCase();
        const linkedProductName = item.productQri
          ? (productDetails[item.productQri]?.name ?? "")
          : "";

        return (
          item.name.toLocaleLowerCase().includes(term) ||
          linkedProductName.toLocaleLowerCase().includes(term)
        );
      }

      return true;
    },
    [productDetails],
  );

  const columns = useMemo(() => {
    const columns = [
      columnHelper.custom("name", {
        header: t("products.list.table.header.product"),
        cell: (props) => {
          return <Link to={props.row.id}>{props.getValue()}</Link>;
        },
      }),
    ];

    if (linkedProductEnabled) {
      columns.push(
        columnHelper.custom(
          (product) =>
            product.productQri
              ? (productDetails[product.productQri]?.name ?? "--")
              : "",
          {
            id: "linkedProduct",
            header: t("products.list.table.header.linkedProduct"),
            cell: ({ row: { original: product }, getValue }) =>
              product.productQri ? (
                <QResourceLabel
                  domain="registry-item"
                  link={product.productQri}
                  openInNewTab
                  isLoading={isResolvingProductNames}
                  data-cy={`linked-products-${product.productQri}`}
                >
                  {getValue()}
                </QResourceLabel>
              ) : (
                <QBox data-cy="linked-products">--</QBox>
              ),
          },
        ),
      );
    }

    if (!!permissions[UserPermissions.MANAGE_PRODUCT]) {
      columns.push(
        columnHelper.menu({
          items: (
            <>
              <TableMenuItem onClick={onEditClick} data-cy="edit-product">
                {t("products.list.actions.edit")}
              </TableMenuItem>
              <TableMenuItem
                onClick={setProductToBeDelete}
                data-cy="delete-product"
                color="red.500"
              >
                {t("products.list.actions.delete")}
              </TableMenuItem>
            </>
          ),
        }),
      );
    }

    return columns;
  }, [
    isResolvingProductNames,
    linkedProductEnabled,
    onEditClick,
    permissions,
    productDetails,
    t,
  ]);

  return (
    <QBodyLayout.Default>
      <QHeader>
        <QTitle>{t("products.list.title")}</QTitle>
        {!!permissions[UserPermissions.MANAGE_PRODUCT] && (
          <QButtonGroup>
            <QButton onClick={createProductClick} data-cy="create-product">
              {t("products.list.actions.create")}
            </QButton>
          </QButtonGroup>
        )}
      </QHeader>
      <Filtering.FilterProvider
        definitions={{}}
        searchTermKey={t("products.list.search")}
      >
        <DataProvider.FixedWithAutoPagination
          data={data}
          isLoading={isLoading && !data.length}
          filterOption={filterOption}
        >
          <QDataTable
            columns={columns}
            data-cy="product-list"
            getRowId={(product: Product) => product.id}
          >
            <Filtering.FormContent />
          </QDataTable>
        </DataProvider.FixedWithAutoPagination>
      </Filtering.FilterProvider>
      <>
        {productToDelete && (
          <QDeleteConfirmationModal
            inProgress={isDeleting}
            title={t("products.delete.modalTitle")}
            message={
              <QText fontSize="sm">
                {t("products.delete.confirmMessage", {
                  productName: productToDelete?.name,
                })}
              </QText>
            }
            onConfirm={() => {
              productToDelete &&
                deleteProduct(productToDelete.id, {
                  onSuccess: () => setProductToBeDelete(undefined),
                });
            }}
            onClose={() => setProductToBeDelete(undefined)}
          />
        )}
      </>
    </QBodyLayout.Default>
  );
};
